import { AlertOctagon, ArrowLeft, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';

/**
 * Full-screen crash UI shared by:
 *   - GlobalErrorBoundary (catches render errors anywhere in the tree)
 *   - RouteError          (React Router 4xx / loader / action errors)
 *
 * Brand-aligned (Codevibe teal #0c3b4d gradient + decorative circles). The
 * details block is collapsed by default - we want the page to feel calm, not
 * scary. Expanding it reveals the error message, stack, current URL, and
 * timestamp so engineers can copy/paste into a bug report.
 *
 * The component is intentionally framework-light: no router hooks, no design-
 * system imports. If the design system itself crashes, this screen still
 * renders.
 */

export interface CrashScreenProps {
  /** Short title shown as the headline. Examples: "Page not found", "Something went wrong". */
  title: string;
  /** Friendly explanation under the headline. */
  description: string;
  /** Optional status code rendered above the title (e.g. 404, 500). */
  statusCode?: number | string;
  /** Optional Error object - drives the details block. */
  error?: unknown;
  /** Primary CTA label. Defaults to "Reload page". */
  primaryLabel?: string;
  /** Primary CTA handler. Defaults to window.location.reload(). */
  onPrimary?: () => void;
  /** Optional secondary CTA. */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export const CrashScreen = ({
  title,
  description,
  statusCode,
  error,
  primaryLabel = 'Reload page',
  onPrimary,
  secondaryLabel,
  onSecondary,
}: CrashScreenProps): React.JSX.Element => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const errorBundle = serializeError(error);
  const timestamp = new Date().toISOString();
  const path =
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';

  const handleCopy = async () => {
    const payload = [
      `# Utazzo crash report`,
      `Title:     ${title}`,
      `Status:    ${statusCode ?? '-'}`,
      `Path:      ${path}`,
      `Timestamp: ${timestamp}`,
      ``,
      `## Error`,
      errorBundle.message,
      ``,
      `## Stack`,
      errorBundle.stack ?? '(no stack)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable - fall back silently.
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Brand gradient background */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0c3b4d 0%, #134a5f 50%, #1a5c75 100%)' }}
        aria-hidden
      />

      {/* Decorative blurs */}
      <div
        className="absolute -top-32 -right-32 h-96 w-96 rounded-full opacity-10"
        style={{ background: '#ffffff' }}
        aria-hidden
      />
      <div
        className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full opacity-[0.06]"
        style={{ background: '#ffffff' }}
        aria-hidden
      />

      {/* Card */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl sm:p-10">
          {/* Icon + status */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <span
                className="absolute inset-0 animate-ping rounded-full opacity-20"
                style={{ background: '#f59e0b' }}
                aria-hidden
              />
              <div
                className="relative flex h-14 w-14 items-center justify-center rounded-full"
                style={{ background: '#fff8e1' }}
              >
                <AlertOctagon className="h-7 w-7" style={{ color: '#f59e0b' }} />
              </div>
            </div>
            <div>
              {statusCode !== undefined && (
                <p className="font-mono text-xs tracking-widest text-slate-500 uppercase">
                  Error {statusCode}
                </p>
              )}
              <h1
                className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl"
                style={{ color: '#0c3b4d' }}
              >
                {title}
              </h1>
            </div>
          </div>

          {/* Description */}
          <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">{description}</p>

          {/* Actions */}
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onPrimary ?? (() => window.location.reload())}
              className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ background: '#0c3b4d' }}
            >
              <RefreshCw className="h-4 w-4" />
              {primaryLabel}
            </button>

            {secondaryLabel && onSecondary && (
              <button
                type="button"
                onClick={onSecondary}
                className="inline-flex items-center gap-2 rounded-md border px-5 py-2.5 text-sm font-semibold transition hover:bg-slate-50"
                style={{ borderColor: '#cfd8dc', color: '#0c3b4d' }}
              >
                <ArrowLeft className="h-4 w-4" />
                {secondaryLabel}
              </button>
            )}
          </div>

          {/* Details */}
          {(errorBundle.message || errorBundle.stack) && (
            <div className="mt-7 rounded-lg border border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-xs font-semibold tracking-wider text-slate-500 uppercase transition hover:text-slate-700"
              >
                <span>Technical details</span>
                <span className="font-mono text-xs">{showDetails ? '−' : '+'}</span>
              </button>

              {showDetails && (
                <div className="border-t border-slate-200 px-4 py-3 text-left">
                  <dl className="grid grid-cols-[100px_1fr] gap-y-1.5 text-xs">
                    <dt className="text-slate-500">Timestamp</dt>
                    <dd className="font-mono text-slate-700">{timestamp}</dd>
                    <dt className="text-slate-500">Path</dt>
                    <dd className="font-mono break-all text-slate-700">{path || '/'}</dd>
                    {statusCode !== undefined && (
                      <>
                        <dt className="text-slate-500">Status</dt>
                        <dd className="font-mono text-slate-700">{statusCode}</dd>
                      </>
                    )}
                  </dl>

                  <p className="mt-3 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                    Message
                  </p>
                  <pre className="mt-1 max-h-32 overflow-auto rounded bg-white px-3 py-2 text-xs break-words whitespace-pre-wrap text-slate-700">
                    {errorBundle.message || '(empty)'}
                  </pre>

                  {errorBundle.stack && (
                    <>
                      <p className="mt-3 text-xs font-semibold tracking-wider text-slate-500 uppercase">
                        Stack
                      </p>
                      <pre className="mt-1 max-h-56 overflow-auto rounded bg-white px-3 py-2 font-mono text-[12px] leading-relaxed whitespace-pre text-slate-600">
                        {errorBundle.stack}
                      </pre>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={handleCopy}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <Copy className="h-3 w-3" />
                    {copied ? 'Copied!' : 'Copy report'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="mt-7 text-center text-xs text-slate-400">
            Utazzo · Codevibe Technology · {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

interface ErrorBundle {
  message: string;
  stack?: string;
}

function serializeError(error: unknown): ErrorBundle {
  if (!error) return { message: '' };
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  try {
    return { message: JSON.stringify(error, null, 2) };
  } catch {
    return { message: String(error) };
  }
}
